import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2Icon, StoreIcon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, CreditCardIcon, SmartphoneIcon, GlobeIcon, ArrowRightIcon, ActivityIcon } from 'lucide-react';
import { KpiCard } from '../../analytics/components/KpiCard';
import { OutletDateRange } from '../components/OutletDateRange';
import { formatMoney } from '../../../shared/lib/money';
import { outletsData, getPortfolioStats, malls } from '../mocks/outletsData';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
export function PortfolioDashboard() {
  const {
    t
  } = useTranslation('common');
  const navigate = useNavigate();
  const stats = getPortfolioStats();
  const [dateRange, setDateRange] = useState('Last 30 days');
  const mallStats = malls.map(mallName => {
    const mallOutlets = outletsData.filter(o => o.mall === mallName);
    const gmv = mallOutlets.reduce((acc, o) => acc + o.periodGmv, 0);
    const count = mallOutlets.length;
    const growth = mallOutlets.reduce((acc, o) => acc + o.growth, 0) / count;
    return {
      name: mallName,
      gmv,
      count,
      growth
    };
  });
  const categoryData = [{
    name: 'F&B',
    value: outletsData.filter(o => o.category === 'F&B').reduce((a, b) => a + b.periodGmv, 0)
  }, {
    name: 'Retail',
    value: outletsData.filter(o => o.category === 'Retail').reduce((a, b) => a + b.periodGmv, 0)
  }, {
    name: 'Luxury',
    value: outletsData.filter(o => o.category === 'Luxury').reduce((a, b) => a + b.periodGmv, 0)
  }, {
    name: 'Services',
    value: outletsData.filter(o => o.category === 'Services').reduce((a, b) => a + b.periodGmv, 0)
  }, {
    name: 'Entertainment',
    value: outletsData.filter(o => o.category === 'Entertainment').reduce((a, b) => a + b.periodGmv, 0)
  }];
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];
  const topPerformers = [...outletsData].sort((a, b) => b.periodGmv - a.periodGmv).slice(0, 3);
  const bottomPerformers = [...outletsData].sort((a, b) => a.periodGmv - b.periodGmv).slice(0, 3);
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-7825">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-7826">
        <div data-id="element-7827">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-7828">
            Portfolio Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-7829">
            Saujana Retail Group • Master Merchant Overview
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-7830">
          <OutletDateRange data-id="element-7831" />
          <button className="px-4 py-2 bg-[var(--brand-primary)] text-[var(--brand-primarytextonwhite)] text-sm font-medium rounded-lg hover:bg-[var(--brand-primaryhover)] transition-colors shadow-sm" data-id="element-7832">
            {t('actions.export')}
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-7833">
        <KpiCard title="Total GMV" value={formatMoney(stats.totalGmv)} icon={<ActivityIcon size={20} data-id="element-7835" />} trend={{
        value: '12.5%',
        isPositive: true
      }} color="blue" data-id="element-7834" />
        <KpiCard title="Transactions" value={stats.totalTxn.toLocaleString()} icon={<CreditCardIcon size={20} data-id="element-7837" />} trend={{
        value: '8.2%',
        isPositive: true
      }} color="green" data-id="element-7836" />
        <KpiCard title="Active Outlets" value={stats.activeOutlets} icon={<StoreIcon size={20} data-id="element-7839" />} trend={{
        value: '2',
        isPositive: true
      }} color="purple" data-id="element-7838" />
        <KpiCard title="Net Settled" value={formatMoney(stats.netSettled)} icon={<Building2Icon size={20} data-id="element-7841" />} trend={{
        value: '11.8%',
        isPositive: true
      }} color="orange" data-id="element-7840" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-7842">
        {/* Mall Comparison */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7843">
          <div className="flex items-center justify-between mb-6" data-id="element-7844">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-7845">
              Mall Performance
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7846">
            {mallStats.map(mall => <div key={mall.name} className="border border-gray-100 rounded-lg p-4 hover:border-blue-100 hover:bg-blue-50/30 transition-colors cursor-pointer" onClick={() => navigate(`/outlets/mall/${mall.name.toLowerCase().replace(/ /g, '-')}`)} data-id="element-7847">
                <div className="flex items-center justify-between mb-3" data-id="element-7848">
                  <h3 className="font-medium text-gray-900" data-id="element-7849">{mall.name}</h3>
                  <ArrowRightIcon size={16} className="text-gray-400" data-id="element-7850" />
                </div>
                <div className="space-y-2" data-id="element-7851">
                  <div className="flex justify-between text-sm" data-id="element-7852">
                    <span className="text-gray-500" data-id="element-7853">GMV</span>
                    <span className="font-medium text-gray-900" data-id="element-7854">
                      {formatMoney(mall.gmv)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-7855">
                    <span className="text-gray-500" data-id="element-7856">Outlets</span>
                    <span className="font-medium text-gray-900" data-id="element-7857">
                      {mall.count}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-7858">
                    <span className="text-gray-500" data-id="element-7859">Growth</span>
                    <span className={`font-medium ${mall.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-7860">
                      {mall.growth >= 0 ? '+' : ''}
                      {mall.growth.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Category Mix */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7861">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-7862">
            Category Mix (GMV)
          </h2>
          <div className="h-64" data-id="element-7863">
            <ResponsiveContainer width="100%" height="100%" data-id="element-7864">
              <PieChart data-id="element-7865">
                <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-7866">
                  {categoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-7867" />)}
                </Pie>
                <Tooltip formatter={(value: number) => formatMoney(value)} data-id="element-7868" />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2" data-id="element-7869">
            {categoryData.map((category, index) => <div key={category.name} className="flex items-center justify-between text-sm" data-id="element-7870">
                <div className="flex items-center gap-2" data-id="element-7871">
                  <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: COLORS[index % COLORS.length]
              }} data-id="element-7872" />
                  <span className="text-gray-600" data-id="element-7873">{category.name}</span>
                </div>
                <span className="font-medium text-gray-900" data-id="element-7874">
                  {formatMoney(category.value)}
                </span>
              </div>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-7875">
        {/* Top Performers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7876">
          <div className="flex items-center justify-between mb-6" data-id="element-7877">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-7878">Top Outlets</h2>
            <button onClick={() => navigate('/outlets/directory')} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-7879">
              View all
            </button>
          </div>
          <div className="space-y-4" data-id="element-7880">
            {topPerformers.map(outlet => <div key={outlet.id} className="flex items-center justify-between cursor-pointer group" onClick={() => navigate(`/outlets/outlet/${outlet.id}`)} data-id="element-7881">
                <div data-id="element-7882">
                  <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-7883">
                    {outlet.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-7884">{outlet.mall}</p>
                </div>
                <div className="text-right" data-id="element-7885">
                  <p className="font-medium text-gray-900" data-id="element-7886">
                    {formatMoney(outlet.periodGmv)}
                  </p>
                  <p className="text-xs text-green-600 flex items-center justify-end gap-1" data-id="element-7887">
                    <TrendingUpIcon size={12} data-id="element-7888" /> +{outlet.growth}%
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Bottom Performers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7889">
          <div className="flex items-center justify-between mb-6" data-id="element-7890">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-7891">
              Needs Attention
            </h2>
          </div>
          <div className="space-y-4" data-id="element-7892">
            {bottomPerformers.map(outlet => <div key={outlet.id} className="flex items-center justify-between cursor-pointer group" onClick={() => navigate(`/outlets/outlet/${outlet.id}`)} data-id="element-7893">
                <div data-id="element-7894">
                  <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-7895">
                    {outlet.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-7896">{outlet.mall}</p>
                </div>
                <div className="text-right" data-id="element-7897">
                  <p className="font-medium text-gray-900" data-id="element-7898">
                    {formatMoney(outlet.periodGmv)}
                  </p>
                  <p className={`text-xs flex items-center justify-end gap-1 ${outlet.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-7899">
                    {outlet.growth >= 0 ? <TrendingUpIcon size={12} data-id="element-7900" /> : <TrendingDownIcon size={12} data-id="element-7901" />}
                    {outlet.growth > 0 ? '+' : ''}
                    {outlet.growth}%
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Alerts Feed */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7902">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-7903">
            Alerts & Exceptions
          </h2>
          <div className="space-y-4" data-id="element-7904">
            <div className="flex gap-3 p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-7905">
              <AlertCircleIcon size={18} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-7906" />
              <div data-id="element-7907">
                <p className="text-sm font-medium text-red-900" data-id="element-7908">
                  Settlement Gap Detected
                </p>
                <p className="text-xs text-red-700 mt-1" data-id="element-7909">
                  QuickFix Mobile shows MYR 1,200 variance in yesterday's
                  settlement batch.
                </p>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-7910">
              <AlertCircleIcon size={18} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-7911" />
              <div data-id="element-7912">
                <p className="text-sm font-medium text-amber-900" data-id="element-7913">
                  Rising Chargebacks
                </p>
                <p className="text-xs text-amber-700 mt-1" data-id="element-7914">
                  Urban Thread chargeback rate reached 0.1% (above 0.05%
                  threshold).
                </p>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100" data-id="element-7915">
              <ActivityIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-7916" />
              <div data-id="element-7917">
                <p className="text-sm font-medium text-blue-900" data-id="element-7918">
                  Reconciliation Break
                </p>
                <p className="text-xs text-blue-700 mt-1" data-id="element-7919">
                  Nyonya Table has 3 unmatched transactions pending review.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}